import { inject, Injectable, signal } from '@angular/core';
import { Invite, InviteType } from '../models/invite';
import { Observable, combineLatest, of, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/shared/services/config-service';
import { MessageService } from 'primeng/api';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class InvitesService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private messageService = inject(MessageService);

  // Signal zur Speicherung der aktuellen Einladungen.
  // Änderungen an diesem Signal lösen eine Reaktion in den Komponenten aus, die darauf hören.
  invites = signal<Invite[]>([]);

  constructor() {
    this.startPolling();
  }

  /**
   * Liefert die Basis-URL der API, abhängig vom InviteType.
   * @param type - Der Typ der Einladung (NectaOne oder VendorCatalog)
   * @returns URL als string
   */
  private getApiUrl(type: InviteType): string {
    return type === InviteType.NectaOne
      ? this.configService.getUrlNectaOneApi()
      : this.configService.getUrlVendorCatalogApi();
  }

  /**
   * Holt Einladungen von beiden APIs (NectaOne und VendorCatalog) und ordnet den jeweiligen InviteType zu.
   * Bei Fehlern wird ein Fehler-Toast angezeigt und ein leerer Array zurückgegeben..
   * @returns Observable mit einer kombinierten Liste von Einladungen.
   */
  private fetchInvites(): Observable<Invite[]> {
    const nectaOneInvites$ = this.http.get<Invite[]>(`${this.configService.getUrlNectaOneApi()}/invites`).pipe(
      map(invites => invites.map(invite => ({ ...invite, type: InviteType.NectaOne }))),
      catchError(error => {
        this.handleError<Invite[]>('Fehler beim Abrufen der NectaOne-Einladungen', error, []);
        return of([]);
      })
    );

    const vendorCatalogInvites$ = this.http.get<Invite[]>(`${this.configService.getUrlVendorCatalogApi()}/invites/by-auth`).pipe(
      map(invites => invites.map(invite => ({ ...invite, type: InviteType.VendorCatalog }))),
      catchError(error => {
        this.handleError<Invite[]>('Fehler beim Abrufen der VendorCatalog-Einladungen', error, []);
        return of([]);
      })
    );

    // Kombiniert beide Observables und führt die Listen zusammen.
    return combineLatest([nectaOneInvites$, vendorCatalogInvites$]).pipe(
      map(([nectaOne, vendorCatalog]) => [...nectaOne, ...vendorCatalog])
    );
  }

  /**
   * Startet das periodische Abrufen der Einladungen.
   * Hier wird alle 10 Sekunden (10000 ms) der fetchInvites()-Aufruf getriggert und
   * das Signal "invites" mit den neuen Daten aktualisiert.
   * 
   * Wurde auf 2min geändert da es nervt..
   */
  private startPolling(): void {
    timer(0, 120000)
      .pipe(switchMap(() => this.fetchInvites()))
      .subscribe(data => this.invites.set(data));
  }

  /**
   * Akzeptiert eine Einladung.
   * @param id - ID der Einladung
   * @param type - Typ der Einladung (NectaOne oder VendorCatalog)
   * @returns Observable, das den Abschluss der Operation signalisiert.
   */
  acceptInvite(invite: Invite): Observable<void> {
    return this.http.post<void>(`${this.getApiUrl(invite.type)}${invite.type === InviteType.NectaOne ? `/invites/` : '/invites/'}${invite.id}/accept`, {});
    //return this.http.post<void>(`${this.getApiUrl(invite.type)}${invite.type === InviteType.NectaOne ? `/${invite.tenantId}/invites/` : '/invites/'}${invite.id}/accept`, {});
  }

  /**
   * Lehnt eine Einladung ab.
   * @param id - ID der Einladung
   * @param type - Typ der Einladung (NectaOne oder VendorCatalog)
   * @returns Observable, das den Abschluss der Operation signalisiert.
   */
  rejectInvite(id: number, type: InviteType): Observable<void> {
    return this.http.post<void>(`${this.getApiUrl(type)}/invites/${id}/reject`, {});
  }

  /**
   * Zentralisierte Fehlerbehandlung.
   * Loggt den Fehler in der Konsole, zeigt einen Toast an und gibt einen Standardwert zurück.
   * @param message - Benutzerdefinierte Fehlermeldung
   * @param error - Der aufgetretene Fehler
   * @param result - Standardrückgabewert im Fehlerfall
   * @returns Observable mit dem Standardwert.
   */
  private handleError<T>(message: string, error: any, result?: T): Observable<T> {
    console.error(message, error);
    this.messageService.add({ severity: 'error', summary: 'Fehler', detail: message });
    return of(result as T);
  }
}
